import { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Breadcrumb,
  Card,
  Alert,
} from 'react-bootstrap'
import { useOrganizationRecentConversations } from 'hooks/use-organization-recent-conversations'
import useUserChats from 'hooks/use-user-chats'
import useChatMessages from 'hooks/use-chat-messages'
import useOrganization from 'hooks/use-organization'
import { useAudioContext } from 'contexts/AudioContext'
import moment from 'moment'
import OrganizationUsersCard from 'components/organization-details-page/organization-users-card'
import ChatsListCard from 'components/user-messages-page/chats-list-card'
import ConversationsCard from 'components/user-messages-page/conversations-card'
import { Chat, Message } from 'components/user-messages-page/types'
import { shouldBlockGhostMessages } from 'utils/ghost-access'

type RecentConversationUser = {
  id: number
  uuid: string
  name: string
  email: string
  phone_number: string
  last_conversation_at: string
}

const OrganizationMessagesPage = () => {
  const { id: orgId } = useParams<{ id: string }>()
  const history = useHistory()
  const { pauseCurrentAudio } = useAudioContext()

  const [selectedUser, setSelectedUser] =
    useState<RecentConversationUser | null>(null)
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [allChats, setAllChats] = useState<Chat[]>([])
  const [hasMoreChats, setHasMoreChats] = useState(true)
  const [filterType, setFilterType] = useState<
    'all' | 'no_response' | 'response' | 'issues'
  >('all')

  const { data: org, isFetching: orgLoading } = useOrganization(orgId)
  const { data: conversationsData, isFetching: usersLoading } =
    useOrganizationRecentConversations(orgId)

  // Fetch user chats with pagination and filter
  const { data: chatsData, isLoading: chatsLoading } = useUserChats(
    selectedUser?.id.toString() || '',
    {
      page: currentPage,
      perPage: 50,
      filter: filterType,
    }
  )

  // Fetch messages for selected chat
  const { data: messagesData, isLoading: messagesLoading } = useChatMessages(
    selectedChatId || ''
  )

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1)
    setSelectedChatId(null) // Also clear selected chat when filter changes
  }, [filterType])

  useEffect(() => {
    if (chatsData?.data?.chats) {
      const newChats = chatsData.data.chats
      const totalPages =
        chatsData?.total_pages || chatsData?.number_of_pages || 1

      if (currentPage === 1) {
        setAllChats(newChats)
      } else {
        setAllChats((prev) => {
          const existingIds = new Set(prev.map((chat: Chat) => chat.id))
          const uniqueNewChats = newChats.filter(
            (chat: Chat) => !existingIds.has(chat.id)
          )
          return [...prev, ...uniqueNewChats]
        })
      }

      setHasMoreChats(currentPage < totalPages)
    }
  }, [chatsData])

  // Reset when user changes
  useEffect(() => {
    setCurrentPage(1)
    setSelectedChatId(null)
    setFilterType('all')
  }, [selectedUser])

  // Stop audio when selectedChatId changes or when component unmounts
  useEffect(() => {
    return () => {
      pauseCurrentAudio()
    }
  }, [selectedChatId, pauseCurrentAudio])

  const handleUserSelect = (user: RecentConversationUser) => {
    setSelectedUser(user)
    setSelectedChatId(null)
    setCurrentPage(1)
    setAllChats([])
    pauseCurrentAudio()
  }

  const handleBackToOrganization = () => {
    history.push(`/organizations/${orgId}`)
  }

  const handleBackToUsers = () => {
    setSelectedUser(null)
    setSelectedChatId(null)
  }

  const handleBackToChats = () => {
    setSelectedChatId(null)
  }

  const handleLoadMoreChats = () => {
    if (hasMoreChats && !chatsLoading) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  const formatTimestamp = (timestamp: string) => {
    return moment(timestamp).format('MMM D, YYYY h:mm A')
  }

  const getOtherPhoneNumber = (chat: Chat) => {
    const otherHandle = chat.chat_handles?.find((handle) => !handle.is_me)
    return otherHandle?.identifier || ''
  }

  const chats: Chat[] = allChats
  const messages: Message[] = messagesData?.data?.chat?.messages || []
  const selectedChat =
    chats.find((c) => c.id.toString() === selectedChatId) || null

  // Check if organization has redacted message history
  const redactMessageHistory = shouldBlockGhostMessages(
    org?.redact_message_history,
    org?.organization_subscription?.customer_product_type
  )

  // Block access and auto-redirect if redact_message_history is enabled
  useEffect(() => {
    if (redactMessageHistory && !orgLoading) {
      // Redirect back to organization detail page after a short delay to show the message
      const timer = setTimeout(() => {
        history.replace(`/organizations/${orgId}`)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [redactMessageHistory, orgLoading, orgId, history])

  if (orgLoading) {
    return (
      <Container
        className="text-center"
        style={{ paddingTop: 'clamp(40px, 15vw, 100px)' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    )
  }

  const breadcrumbContent = (
    <Breadcrumb>
      <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
      <Breadcrumb.Item href="/organizations">Organizations</Breadcrumb.Item>
      <Breadcrumb.Item href={`/organizations/${orgId}`}>
        {org?.name || 'Organization Details'}
      </Breadcrumb.Item>
      <Breadcrumb.Item active>Organization User Chats</Breadcrumb.Item>
    </Breadcrumb>
  )

  const backButton = (
    <Button
      variant="success"
      onClick={handleBackToOrganization}
      style={{
        marginRight: 'clamp(8px, 2vw, 16px)',
        padding: 'clamp(6px, 1.5vw, 10px)',
        fontSize: 'clamp(14px, 3vw, 16px)',
      }}
    >
      <ion-icon name="chevron-back" />
    </Button>
  )

  // Show blocked message if redact_message_history is enabled
  if (redactMessageHistory) {
    return (
      <Container className="mb-4">
        {breadcrumbContent}

        <div className="d-flex align-items-center mb-2">
          {backButton}
          <h3 className="mb-0" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>
            {org?.name || 'Organization Chats'}
          </h3>
        </div>

        <Alert variant="danger" className="mt-3">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>
            Message history is redacted for this organization due to active
            partner integration. Access to chat messages is restricted.
          </p>
          <hr />
          <p className="mb-0">
            You will be redirected back to the organization details page in a
            moment...
          </p>
        </Alert>
      </Container>
    )
  }

  return (
    <Container className="mb-2">
      {breadcrumbContent}

      <div className="d-flex align-items-center mb-2">
        {backButton}
        <div>
          <h4 className="mb-0" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>
            {org?.name || 'Organization'} - Users Conversation
          </h4>
        </div>
      </div>
      <Row style={{ margin: '0 clamp(-8px, -2vw, -12px)' }}>
        <Col
          xs={12}
          lg={3}
          className={selectedUser ? 'd-none d-lg-block' : ''}
          style={{ padding: '0 clamp(4px, 1vw, 8px)' }}
        >
          <OrganizationUsersCard
            users={conversationsData?.data || []}
            onSelectUser={handleUserSelect}
            selectedUser={selectedUser}
            loading={usersLoading}
          />
        </Col>

        <Col
          xs={12}
          lg={4}
          className={!selectedUser || selectedChatId ? 'd-none d-lg-block' : ''}
          style={{ padding: '0 clamp(4px, 1vw, 8px)' }}
        >
          <div>
            {selectedUser ? (
              <ChatsListCard
                chats={chats}
                chatsLoading={chatsLoading}
                onSelectChat={setSelectedChatId}
                hasMoreChats={hasMoreChats}
                onLoadMoreChats={handleLoadMoreChats}
                selectedChatId={selectedChatId}
                filterType={filterType}
                onFilterChange={setFilterType}
                onBack={handleBackToUsers}
              />
            ) : (
              <Card
                className="mt-4"
                style={{
                  height: 'clamp(400px, 70vh, 700px)',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '400px',
                }}
              >
                <Card.Header
                  className="d-flex align-items-center"
                  style={{ padding: 'clamp(12px, 3vw, 10px)' }}
                >
                  <Button
                    variant="link"
                    onClick={handleBackToUsers}
                    className="me-3 d-lg-none p-0"
                    style={{
                      fontSize: 'clamp(20px, 4vw, 24px)',
                      color: '#4ea6ff',
                    }}
                  >
                    <ion-icon name="chevron-back-outline" />
                  </Button>
                  <h3
                    className="mb-0"
                    style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}
                  >
                    User Chats
                  </h3>
                </Card.Header>
                <Card.Body className="d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <h5>Select a user</h5>
                    <p className="text-muted">
                      Choose a user from the left to view their chats
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>
        </Col>

        <Col
          xs={12}
          lg={5}
          className={!selectedChatId ? 'd-none d-lg-block' : ''}
          style={{ padding: '0 clamp(4px, 1vw, 8px)' }}
        >
          {selectedUser ? (
            <ConversationsCard
              selectedChat={selectedChat}
              messages={messages}
              messagesLoading={messagesLoading}
              getOtherPhoneNumber={getOtherPhoneNumber}
              formatTimestamp={formatTimestamp}
              onBack={handleBackToChats}
            />
          ) : (
            <Card
              className="mt-4 d-flex flex-column"
              style={{
                height: 'clamp(400px, 70vh, 700px)',
                minHeight: '400px',
              }}
            >
              <Card.Header style={{ padding: 'clamp(12px, 3vw, 10px)' }}>
                <h5 style={{ fontSize: 'clamp(16px, 3.5vw, 24px)', margin: 0 }}>
                  Conversations
                </h5>
              </Card.Header>
              <Card.Body className="d-flex align-items-center justify-content-center flex-grow-1">
                <div className="text-center">
                  <h5>Select a user and chat</h5>
                  <p className="text-muted">
                    Choose a user from the left and then select a chat to view
                    messages
                  </p>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  )
}

export default OrganizationMessagesPage
